import { Injectable } from '@angular/core';
import { Vehicle } from '@models/vehicle.model';
import { catchError, combineLatest, map, Observable, of, switchMap, tap } from 'rxjs';
import { FileService } from './file.service';
import { DbService } from './db.service';
import { Filter } from '@models/filter.model';
import { Sort } from '@models/sort.model';
import { environment } from '@environments/environment';
import { ListHelper } from '@classes/list-helper';
import { ObjectHelper } from '@classes/object-helper';
import { VINDecoderData } from '@models/vin-decoder-data.model';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AllowedVehicleService } from './allowed-vehicle.service';
import { AllowedVehicle } from '@models/allowed-vehicle.model';
import { Cache } from '@classes/cache';
import { VINDecoderResponse } from '@models/vin-decoder-response.model';

const COLLECTION = 'vehicles';
const IMAGES_FOLDER = 'images/vehicle';

@Injectable({
    providedIn: 'root'
})
export class VehicleService {
    constructor(
        private fileService: FileService,
        private dbService: DbService,
        private http: HttpClient,
        private allowedVehicleService: AllowedVehicleService
    ) {}

    getVehicles(userId: string): Observable<Vehicle[]> {
        const filters: Filter[] = [{ field: 'userId', operator: '==', value: userId }];
        const sort: Sort = { field: 'creationDate', direction: 'desc' };
        return this.dbService.getList<Vehicle>(COLLECTION, filters, sort).pipe(
            map(vehicles => {
                vehicles.forEach(vehicle => {
                    this.fixDates(vehicle);
                });
                return vehicles;
            })
        );
    }

    getVehicle(id: string): Observable<Vehicle> {
        return this.dbService.getObj<Vehicle>(COLLECTION, id).pipe(
            map(vehicle => {
                this.fixDates(vehicle);
                return vehicle;
            })
        );
    }

    getActiveVehicles(userIds: string[]): Observable<Vehicle[]> {
        if (userIds.length === 0) {
            return of([]);
        }

        const commonFilters: Filter[] = [
            { field: 'active', operator: '==', value: true }
        ];
        const vehiclesList$: Observable<Vehicle[]>[] = [];
        const userIdsList = ListHelper.splitListIntoChunks(userIds, environment.firebaseInClauseLimit);
        userIdsList.forEach(value => {
            const filters = [...commonFilters];
            filters.push({ field: 'userId', operator: 'in', value });
            const vehicles$ = this.dbService.getList<Vehicle>(COLLECTION, filters);
            vehiclesList$.push(vehicles$);
        });
        return combineLatest(vehiclesList$).pipe(
            map(vehiclesList => {
                const vehicles = vehiclesList.flat();
                vehicles.forEach(vehicle => {
                    this.fixDates(vehicle);
                });
                return vehicles;
            })
        );
    }

    getActiveVehicle(userId: string): Observable<Vehicle> {
        const filters: Filter[] = [
            { field: 'userId', operator: '==', value: userId },
            { field: 'active', operator: '==', value: true }
        ];
        return this.dbService.getList<Vehicle>(COLLECTION, filters).pipe(
            map(x => x[0]),
            map(vehicle => {
                this.fixDates(vehicle);
                return vehicle;
            })
        );
    }

    saveVehicle(vehicle: Vehicle): Observable<Vehicle> {
        // If they have supplied a photo, upload it
        if (vehicle.registrationPhotoFile || vehicle.insurancePhotoFile) {
            const rootFileName = `${vehicle.year}-${vehicle.make}-${vehicle.model}`;
            const registrationFileName = `${rootFileName}-registration`;
            const insuranceFileName = `${rootFileName}-insurance`;
            const registrationPhoto$ = vehicle.registrationPhotoFile ?
                this.fileService.uploadFile(IMAGES_FOLDER, vehicle.registrationPhotoFile, registrationFileName) :
                of(vehicle.registrationPhoto);
            const insurancePhoto$ = vehicle.insurancePhotoFile ?
                this.fileService.uploadFile(IMAGES_FOLDER, vehicle.insurancePhotoFile, insuranceFileName) :
                of(vehicle.insurancePhoto);
            const save$ = combineLatest([registrationPhoto$, insurancePhoto$]).pipe(
                switchMap(([registrationPhoto, insurancePhoto]) => this.save({ ...vehicle,
                    registrationPhoto: registrationPhoto,
                    registrationPhotoFile: undefined,
                    insurancePhoto: insurancePhoto,
                    insurancePhotoFile: undefined
                }))
            );

            // Delete the previous photo(s) (if any)
            if ((vehicle.registrationPhoto && vehicle.registrationPhotoFile) ||
                (vehicle.insurancePhoto && vehicle.insurancePhotoFile)) {
                const registrationName = vehicle.registrationPhotoFile ?
                    this.fileService.getFileName(vehicle.registrationPhoto, IMAGES_FOLDER) :
                    null;
                const deleteFrontFile$ = registrationName ? this.fileService.deleteFile(IMAGES_FOLDER, registrationName).pipe(
                    catchError(() => of([])) // Don't worry if the deletion fails
                ) : of('');
                const insuranceName = vehicle.insurancePhotoFile ?
                    this.fileService.getFileName(vehicle.insurancePhoto, IMAGES_FOLDER) :
                    null;
                const deleteBackFile$ = insuranceName ? this.fileService.deleteFile(IMAGES_FOLDER, insuranceName).pipe(
                    catchError(() => of([])) // Don't worry if the deletion fails
                ) : of('');
                return combineLatest([save$, deleteFrontFile$, deleteBackFile$]).pipe(
                    map(([save]) => save)
                );
            }

            return save$;
        }

        return this.save(vehicle);
    }

    deleteVehicle(vehicle: Vehicle): Observable<string> {
        const delete$ = this.dbService.deleteObj(COLLECTION, vehicle.id);

        // Delete the registration photo (if any)
        if (vehicle.registrationPhoto) {
            const name = this.fileService.getFileName(vehicle.registrationPhoto, IMAGES_FOLDER);
            const deleteFile$ = this.fileService.deleteFile(IMAGES_FOLDER, name).pipe(
                catchError(() => of([])) // Don't worry if the deletion fails
            );
            return combineLatest([delete$, deleteFile$]).pipe(
                map(([id]) => id)
            );
        }

        return delete$;
    }

    updateVehicles(vehicles: Vehicle[]): Observable<Vehicle[]> {
        return this.dbService.updateList(COLLECTION, vehicles);
    }

    decodeVIN(vin: string): Observable<VINDecoderData> {
        // return of({ year: 2024, make: 'Toyota', model: 'Tundra' }); // Testing
        // return of({ year: 2021, make: 'Nissan', model: 'Titan' }); // Testing
        // return of({ year: 2021, make: 'Nissan' }); // Testing
        // return of({ year: 2021 }); // Testing
        // return of({ year: 0 }); // Testing
        // 1FTEW1E54JFB30392
        // 1GCUDDED9RZ129873
        // 1C6SRFFT2MN586025
        // 3GTU9DED5MG370968
        // 1C6HJTFG9ML572069
        // 1N6AD0ER4GN721965
        // 1N6AA1EE3MN528567
        // 5TFLA5DB1RX213322 - Toyota Tundra 2024
        // 5TFLA5AB2RX039331 - Toyota Tundra 2024
        // 3TMKB5FN6RM014224 - Toyota Tacoma 2024
        // 1FT8W2BM2REC24884 - Ford F-250 2024
        // 1FT6W7L70RWG27270 - Ford F-250 2024 (Electric)
        // 1FTRX18L1YKA06915 = Ford F-150 2000
        // 1FTWW32F72EC98368 = Ford F-250 2002
        // 1FTPX125X5NB58817 = Ford F-150 2005
        // 5TFWA5DB5RX185327 - Toyota Tundra 2024
        // 5TEHN72N54Z389309 - Toyota Tundra 2004
        // 1GT5UMEY0TF122410 - GMC Sierra 2500 2026
        // 1C6HJTAG9RL123229 - Jeep Gladiator 2024
        // 1GT5UMEY0TF122410 - GMC Sierra 2500 2026
        // 7FCTGAAA0PN017071 - Rivian R1T 2023
        // 7FCTGBAA9SN037463 - Rivian R1T 2025
        // 1C8RR7FG3JS122348 - RAM 1500 2018 - API only returns year and manufacturer
        // 1GCGSBEC9P1176119 - Chevrolet Colorado 2023
        // 1GCFG15T571196262 - Chevrolet Express 2007
        const url = `${environment.apiBaseUrl}/vehicles/decode-vin/${vin}`;
        return this.http.get<VINDecoderData>(url);
    }

    isVINFound(vin: string): Observable<boolean> {
        const filters: Filter[] = [{ field: 'vin', operator: '==', value: vin }];
        return this.dbService.getCount(COLLECTION, filters).pipe(
            map(count => count > 0)
        );
    }

    private save(vehicle: Vehicle): Observable<Vehicle> {
        return this.dbService.saveObj(COLLECTION, vehicle);
    }

    private fixDates(vehicle: Vehicle): void {
        ObjectHelper.fixDate(vehicle, 'registrationExpDate');
        ObjectHelper.fixDate(vehicle, 'insuranceExpDate');
    }
}
